<template>
  <div class="ps-table-wrap">
    <div v-if="isLoading" class="loading_state">
      <spinner />
    </div>

    <empty-state
      v-else-if="!records.length"
      :title="
        $t('CAMPAIGN.LLM_CHATBOT.AI_BROADCAST_LIST.CALL_CAMPAIGN.NO_RECORDS')
      "
    />

    <ve-table
      v-else
      :fixed-header="true"
      :columns="columns"
      :max-height="'calc(100vh - 210px)'"
      :sort-option="sortOption"
      :table-data="records"
      :border-around="true"
    />
  </div>
</template>

<script>
import { VeTable } from 'vue-easytable';
import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import WootButton from 'dashboard/components/ui/WootButton.vue';
import Label from 'dashboard/components/ui/Label';
import responsiveMixin from 'shared/mixins/responsiveMixin.js';

export default {
  name: 'CampaignListTable',
  components: {
    VeTable,
    Spinner,
    EmptyState,
  },
  mixins: [responsiveMixin],
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    records: {
      type: Array,
      default: () => [],
    },
    meta: {
      type: Object,
      default: () => ({}),
    },
    kind: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      sortConfig: {},
      sortOption: {
        sortAlways: true,
        sortIcons: {
          ascend: 'top-arrow',
          descend: 'bottom-arrow',
        },
        sortChange: params => {
          this.sortConfig = params;
          this.$emit('on-sort-change', params);
        },
      },
    };
  },
  computed: {
    columns() {
      const matches = {};

      matches.name = {
        field: 'name',
        key: 'name',
        title: 'Campaign Name',
        width: 250,
        align: 'left',
        fixed: this.isMobile ? false : 'left',
        ...(this.isSortable('name') && {
          sortBy: this.getSortByKey('name'),
        }),
        renderBodyCell: ({ row }) => (
          <h6
            class="font_regular text_14 font_500 campaign-name-link"
            onClick={() => this.$emit('on-name-click', row)}
          >
            {row.name}
          </h6>
        ),
      };

      matches.inbox_name = {
        field: 'inbox_name',
        key: 'inbox_name',
        title: 'Inbox',
        width: 180,
        align: 'left',
        ...(this.isSortable('inbox_name') && {
          sortBy: this.getSortByKey('inbox_name'),
        }),
        renderBodyCell: ({ row }) => <span>{row.inbox?.name || '-'}</span>,
      };

      matches.assistant = {
        field: 'assistant',
        key: 'assistant',
        title: 'Resources',
        width: 200,
        align: 'left',
        renderBodyCell: ({ row }) => {
          const assistantName = row.assistant?.name || '-';
          const llmModel = row.assistant?.llm_model || '';
          const workerCount = row.worker_count || 0;
          return (
            <div class="resources-cell">
              <span class="assistant-name">{assistantName}</span>
              <span class="resources-line">
                {llmModel && <span class="llm-model">{llmModel}</span>}
                <span class="separator"> | </span>
                <span class="worker-count">{workerCount} Worker</span>
              </span>
            </div>
          );
        },
      };

      matches.date_range = {
        field: 'date_range',
        key: 'date_range',
        title: 'Scheduled Date',
        width: 200,
        align: 'center',
        renderBodyCell: data => {
          const { row } = data;
          const startDate = this.formatDate(row.start_date);
          const endDate = this.formatDate(row.end_date);
          const scheduledAt = this.formatDate(row.scheduled_at);
          if (scheduledAt !== '-') {
            data.key = 'scheduled_at';
          }

          const time = `${this.formatTime(row.time_start)} - ${this.formatTime(
            row.time_end
          )}`;

          return (
            <div class="date-range-cell align-center">
              {startDate !== '-' && endDate !== '-' ? (
                <div>
                  <span>{startDate}</span>
                  {endDate && <span class="separator"> → </span>}
                  <span>{endDate}</span>
                </div>
              ) : (
                scheduledAt !== '-' && <span>{scheduledAt}</span>
              )}

              {time !== '- - -' && (
                <div class="time-cell">
                  <span>{time}</span>
                </div>
              )}
            </div>
          );
        },
      };

      matches.campaign_recipients = {
        field: 'campaign_recipients',
        key: 'campaign_recipients',
        title: 'Recipients',
        width: 180,
        align: 'center',
        ...(this.isSortable('campaign_recipients') && {
          sortBy: this.getSortByKey('campaign_recipients'),
        }),
        renderBodyCell: ({ row }) => {
          const total = row.campaign_recipients || 0;
          return (
            <div class="recipients-cell">
              <p class="total-count">{total}</p>
            </div>
          );
        },
      };

      if (this.kind === 'call') {
        matches.call_results = {
          field: 'call_results',
          key: 'call_results',
          title: 'Results',
          width: 180,
          align: 'right',
          renderBodyCell: ({ row }) => {
            const success = row.success_count || 0;
            const failure = row.failure_count || 0;
            const unknown = row.unknown_count || 0;
            return (
              <div class="results-cell">
                <span class="result-item success">{success}</span>
                <span class="result-item failure">{failure}</span>
                <span class="result-item unknown">{unknown}</span>
              </div>
            );
          },
        };
      }

      matches.status = {
        field: 'status',
        key: 'status',
        title: 'Status',
        width: 150,
        align: 'center',
        ...(this.isSortable('status') && {
          sortBy: this.getSortByKey('status'),
        }),
        renderBodyCell: ({ row }) => (
          <Label
            title={this.formatStatus(row.status)}
            colorScheme={this.getStatusColor(row.status)}
          />
        ),
      };

      matches.buttons = {
        field: 'buttons',
        key: 'buttons',
        title: 'Action',
        align: 'left',
        width: 120,
        renderBodyCell: row => {
          const isAllowDel = ['active', 'pending', 'running'].includes(
            row.row.status
          );

          return (
            <div class="button-wrapper">
              {/* Edit Button */}
              <WootButton
                variant="link"
                icon="edit"
                color-scheme="secondary"
                onClick={() => this.$emit('on-edit-click', row.row)}
              ></WootButton>

              {/* Delete Button */}
              {isAllowDel && (
                <WootButton
                  variant="link"
                  icon="dismiss-circle"
                  color-scheme="secondary"
                  onClick={() => this.$emit('on-delete-click', row.row)}
                ></WootButton>
              )}
            </div>
          );
        },
      };

      return Object.values(matches);
    },
  },
  methods: {
    formatTime(time) {
      if (time === undefined || time === null || time === 0) return '-';
      const date = new Date(time);
      const hours = date.getHours();
      const minutes = date.getMinutes();
      return `${hours}:${minutes < 10 ? `0${minutes}` : minutes}`;
    },
    getSortByKey(key) {
      const value = this.sortConfig[key];
      return value ? String(value) : '';
    },
    isSortable(key) {
      return Array.isArray(this.meta?.sorts) && this.meta.sorts?.includes(key);
    },
    formatDuration(duration) {
      if (duration === undefined || duration === null || duration === 0)
        return '-';
      const minutes = Math.floor(duration);
      const seconds = Math.round((duration - minutes) * 60);
      if (minutes > 0 && seconds === 0) {
        return `${minutes}m`;
      }
      return `${minutes}m ${seconds}s`;
    },
    formatStatus(status) {
      if (!status) return '-';
      return status.charAt(0).toUpperCase() + status.slice(1);
    },
    getStatusColor(status) {
      switch (status) {
        case 'pending':
          return 'warning';
        case 'active':
        case 'running':
          return 'success';
        case 'completed':
          return 'secondary';
        case 'deactivate':
          return 'warning';
        default:
          return 'secondary';
      }
    },
    formatDate(date) {
      if (!date) return '-';
      const d = new Date(date);
      return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~dashboard/assets/scss/widgets/_ps_table.scss';

.loading_state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
}

.no-btn {
  display: none;
}

.button-wrapper {
  display: flex;
  gap: 8px;
}

.recipients-cell {
  display: flex;
  flex-direction: column;
  align-items: center;

  .total-count {
    font-weight: 600;
    font-size: 14px;
  }

  .breakdown {
    font-size: 11px;
    color: var(--s-600);
    margin-top: 2px;
  }
}

.resources-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;

  .assistant-name {
    font-weight: 500;
    font-size: 14px;
  }

  .resources-line {
    font-size: 12px;
    color: var(--s-600);
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;

    .llm-model {
      background-color: var(--s-100);
      padding: 1px 6px;
      border-radius: 3px;
    }

    .separator {
      color: var(--s-400);
    }

    .worker-count {
      font-weight: 500;
    }
  }
}

.date-range-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  font-size: 13px;

  .separator {
    color: var(--s-500);
  }
}

.results-cell {
  display: flex;
  gap: 8px;
  justify-content: flex-end;

  .result-item {
    display: inline-block;
    min-width: 35px;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    text-align: center;

    &.success {
      background-color: var(--g-100);
      color: var(--g-800);
    }

    &.failure {
      background-color: var(--r-100);
      color: var(--r-800);
    }

    &.unknown {
      background-color: var(--s-100);
      color: var(--s-800);
    }
  }
}

.stats-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;

  .stat-item {
    font-size: 13px;

    &.duration {
      color: var(--s-600);
    }
  }
}

.justify-center {
  justify-content: center;
}

.campaign-name-link {
  cursor: pointer;
  color: var(--c-800);
  transition: color 0.2s ease;

  &:hover {
    color: var(--c-700);
    text-decoration: underline;
  }
}
</style>
